import React, { useState, useRef, useEffect } from 'react';
import { X, Mail, Send, Square, Save, Trash2, RefreshCw } from 'lucide-react';
import { get, post, put, del } from '../utils/fetcher';

const DEFAULT = {
    subject: 'Empire Premium',
    bgColor: '#0c0c0d',
    cardColor: '#141416',
    imageUrl: '',
    imageType: 'icon',       // 'icon' | 'banner'
    imageSize: 80,
    title: 'EMPIRE',
    titleColor: '#ffffff',
    titleSize: 26,
    text: '',
    textColor: '#e8e8ea',
    btnLabel: 'Accéder',
    btnLink: '',
    btnStyle: 'outline',     // 'fill' | 'outline'
    btnColor: '#e0b84c',
    showLinkBelow: true,
}

const Field = ({ label, children }) => (
    <div className='mb-field'>
        <label className='fr cw fs12'>{label}</label>
        {children}
    </div>
)

const MailBuilder = ({ emails = [], onClose }) => {
    const [cfg, setCfg] = useState(DEFAULT)
    const [sending, setSending] = useState(false)
    const [delay, setDelay] = useState(1)       // secondes entre chaque envoi
    const [progress, setProgress] = useState({ sent: 0, failed: 0, done: 0, total: 0 })
    const cancelRef = useRef(false)

    // presets
    const [presets, setPresets] = useState([])
    const [selectedPreset, setSelectedPreset] = useState('')
    const [presetName, setPresetName] = useState('')

    const set = (k, v) => setCfg(c => ({ ...c, [k]: v }))

    const loadPresets = async () => {
        const res = await get('/mail/presets')
        setPresets(Array.isArray(res) ? res : [])
    }
    useEffect(() => { loadPresets() }, [])

    const applyPreset = (id) => {
        setSelectedPreset(id)
        const p = presets.find(x => x.id === id)
        if (p) { setCfg({ ...DEFAULT, ...(p.config || {}) }); setPresetName(p.name) }
    }
    const savePreset = async () => {
        if (!presetName.trim()) return
        const res = await post('/mail/presets', { name: presetName.trim(), config: cfg })
        await loadPresets()
        if (res?.id) setSelectedPreset(res.id)
    }
    const updatePreset = async () => {
        if (!selectedPreset) return
        await put(`/mail/presets/${selectedPreset}`, { name: presetName.trim(), config: cfg })
        await loadPresets()
    }
    const deletePreset = async () => {
        if (!selectedPreset) return
        await del(`/mail/presets/${selectedPreset}`)
        setSelectedPreset(''); setPresetName('')
        await loadPresets()
    }

    const send = async () => {
        if (!emails.length || sending) return
        cancelRef.current = false
        setSending(true)
        setProgress({ sent: 0, failed: 0, done: 0, total: emails.length })
        const d = Math.max(0, parseFloat(delay) || 0)
        for (let i = 0; i < emails.length; i++) {
            if (cancelRef.current) break
            // eslint-disable-next-line no-await-in-loop
            const res = await post('/mail/send', { emails: [emails[i]], subject: cfg.subject, config: cfg })
            setProgress(p => ({
                sent: p.sent + (res?.sent || 0),
                failed: p.failed + (res?.sent ? 0 : 1),
                done: p.done + 1,
                total: p.total,
            }))
            if (i < emails.length - 1 && d > 0 && !cancelRef.current) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise(r => setTimeout(r, d * 1000))
            }
        }
        setSending(false)
    }
    const stop = () => { cancelRef.current = true }

    const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0

    const btnStyle = cfg.btnStyle === 'fill'
        ? { background: cfg.btnColor, color: '#000', borderColor: cfg.btnColor }
        : { background: 'transparent', color: cfg.btnColor, borderColor: cfg.btnColor }

    return (
        <div className='modal-overlay' onClick={onClose}>
            <div className='mail-builder' onClick={(e) => e.stopPropagation()}>
                <div className='df aic jcsb mb2'>
                    <p className='fb cw fs18 df aic gap1'><Mail size={18} color='#fff' /> Composer un email — {emails.length} destinataire(s)</p>
                    <button onClick={onClose} className='pay-act-btn'><X size={18} color='#fff' /></button>
                </div>

                {/* Presets */}
                <div className='mb-presets df aic gap1 fw mb2'>
                    <select value={selectedPreset} onChange={(e) => applyPreset(e.currentTarget.value)} className='ub-input2' style={{ maxWidth: '200px' }}>
                        <option value=''>— Charger un preset —</option>
                        {presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <input className='ub-input2' style={{ maxWidth: '180px' }} placeholder='Nom du preset' value={presetName} onChange={(e) => setPresetName(e.currentTarget.value)} />
                    <button onClick={savePreset} disabled={!presetName.trim()} className='btn cw df aic gap1'><Save size={14} color='#fff' /> Sauvegarder</button>
                    {selectedPreset && <button onClick={updatePreset} className='btn cw df aic gap1'><RefreshCw size={14} color='#fff' /> Mettre à jour</button>}
                    {selectedPreset && <button onClick={deletePreset} className='btn cw df aic gap1'><Trash2 size={14} color='#f71717' /> Supprimer</button>}
                </div>

                <div className='mb-body df gap2'>
                    {/* ------- ÉDITION ------- */}
                    <div className='mb-left'>
                        <p className='mb-section'>Contenu</p>
                        <Field label='Objet'><input className='ub-input2' value={cfg.subject} onChange={e => set('subject', e.currentTarget.value)} /></Field>
                        <Field label='Titre'><input className='ub-input2' value={cfg.title} onChange={e => set('title', e.currentTarget.value)} /></Field>
                        <div className='df gap1'>
                            <Field label='Taille titre'><input type='number' className='ub-input2' value={cfg.titleSize} onChange={e => set('titleSize', e.currentTarget.value)} /></Field>
                            <Field label='Couleur titre'><input type='color' className='mb-color' value={cfg.titleColor} onChange={e => set('titleColor', e.currentTarget.value)} /></Field>
                        </div>
                        <Field label='Texte'><textarea rows={4} className='ub-input2' value={cfg.text} onChange={e => set('text', e.currentTarget.value)} placeholder='Votre message...'></textarea></Field>
                        <Field label='Couleur texte'><input type='color' className='mb-color' value={cfg.textColor} onChange={e => set('textColor', e.currentTarget.value)} /></Field>

                        <p className='mb-section'>Image</p>
                        <Field label="URL de l'image"><input className='ub-input2' value={cfg.imageUrl} onChange={e => set('imageUrl', e.currentTarget.value)} placeholder='https://...' /></Field>
                        <div className='df gap1'>
                            <Field label='Type'>
                                <div className='df gap1'>
                                    <button onClick={() => set('imageType', 'icon')} className={`btn ${cfg.imageType === 'icon' ? 'cs' : 'cw'}`}>Icône</button>
                                    <button onClick={() => set('imageType', 'banner')} className={`btn ${cfg.imageType === 'banner' ? 'cs' : 'cw'}`}>Bannière</button>
                                </div>
                            </Field>
                            {cfg.imageType === 'icon' && (
                                <Field label='Taille (px)'><input type='number' className='ub-input2' value={cfg.imageSize} onChange={e => set('imageSize', e.currentTarget.value)} /></Field>
                            )}
                        </div>

                        <p className='mb-section'>Bouton</p>
                        <div className='df gap1'>
                            <Field label='Libellé'><input className='ub-input2' value={cfg.btnLabel} onChange={e => set('btnLabel', e.currentTarget.value)} /></Field>
                            <Field label='Couleur'><input type='color' className='mb-color' value={cfg.btnColor} onChange={e => set('btnColor', e.currentTarget.value)} /></Field>
                        </div>
                        <Field label='Lien (URL)'><input className='ub-input2' value={cfg.btnLink} onChange={e => set('btnLink', e.currentTarget.value)} placeholder='https://...' /></Field>
                        <div className='df gap1 aic fw'>
                            <button onClick={() => set('btnStyle', 'fill')} className={`btn ${cfg.btnStyle === 'fill' ? 'cs' : 'cw'}`}>Rempli</button>
                            <button onClick={() => set('btnStyle', 'outline')} className={`btn ${cfg.btnStyle === 'outline' ? 'cs' : 'cw'}`}>Contour</button>
                            <label className='df aic gap1 cw fr fs12' style={{ cursor: 'pointer' }}>
                                <input type='checkbox' checked={cfg.showLinkBelow} onChange={() => set('showLinkBelow', !cfg.showLinkBelow)} /> Lien en dessous
                            </label>
                        </div>

                        <p className='mb-section'>Fond</p>
                        <div className='df gap1'>
                            <Field label='Fond page'><input type='color' className='mb-color' value={cfg.bgColor} onChange={e => set('bgColor', e.currentTarget.value)} /></Field>
                            <Field label='Fond carte'><input type='color' className='mb-color' value={cfg.cardColor} onChange={e => set('cardColor', e.currentTarget.value)} /></Field>
                        </div>
                    </div>

                    {/* ------- RÉSULTAT ------- */}
                    <div className='mb-right'>
                        <div className='mb-preview' style={{ background: cfg.bgColor }}>
                            <div className='mb-card' style={{ background: cfg.cardColor }}>
                                {cfg.imageUrl && (cfg.imageType === 'banner'
                                    ? <img src={cfg.imageUrl} alt='' className='mb-banner' />
                                    : <img src={cfg.imageUrl} alt='' className='mb-iconimg' style={{ width: `${cfg.imageSize}px`, height: `${cfg.imageSize}px` }} />)}
                                {cfg.title && <div className='mb-title' style={{ color: cfg.titleColor, fontSize: `${cfg.titleSize}px` }}>{cfg.title}</div>}
                                <div className='mb-text' style={{ color: cfg.textColor }}>{cfg.text || 'Votre message...'}</div>
                                {cfg.btnLabel && cfg.btnLink && (
                                    <a className='mb-btn' style={btnStyle} href={cfg.btnLink} target='_blank' rel='noreferrer'>{cfg.btnLabel}</a>
                                )}
                                {cfg.btnLink && cfg.showLinkBelow && (
                                    <div className='mb-linkbelow' style={{ color: cfg.textColor }}>Si le bouton ne fonctionne pas :<br /><span style={{ color: cfg.btnColor }}>{cfg.btnLink}</span></div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Progression */}
                {(sending || progress.done > 0) && (
                    <div className='mb-progress mt2'>
                        <div className='mb-bar'><div className='mb-bar-fill' style={{ width: `${pct}%` }}></div></div>
                        <p className='cw fr fs12 mt1'>{progress.done}/{progress.total} — envoyés {progress.sent} · échecs {progress.failed}</p>
                    </div>
                )}

                <div className='df aic jcsb fw gap1 mt2'>
                    <label className='df aic gap1 cw fr fs12'>
                        Délai entre envois (s)
                        <input type='number' min='0' step='0.5' value={delay} onChange={(e) => setDelay(e.currentTarget.value)} className='ub-num' style={{ width: '80px' }} />
                    </label>
                    {sending ? (
                        <button onClick={stop} className='btn cw df aic jcc gap1'><Square size={14} color='#f71717' /> Stop</button>
                    ) : (
                        <button onClick={send} disabled={!emails.length} className='btn cs df aic jcc gap1'><Send size={15} color='#fff' /> Envoyer ({emails.length})</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MailBuilder;
